import format from 'date-fns/format'
import XLSX from 'xlsx'
import FileSaver from 'file-saver'
import { RevenueReportOnDeliver, RevenueReportOnOrder, RevenueType } from 'types/revenue'
import { CheckoutItemType } from 'types/checkoutItem'
import { CheckoutShoppingBagType, OrderType } from 'types/checkout'
import { DELIVERY_FEE_TAX_RATE, PRODUCT_TAX_RATE, SHOPPING_BAG_TAX_RATE } from 'constants/tax'
import { BASE_TRANSFER_FEE, TRANSFER_FEE_RATE } from 'constants/stripe'

export const sumOrdersPrice = (orders: OrderType[], isIncludingDiscount?: boolean): number => {
  return orders.reduce((prev, order) => {
    const itemsTotal = sumCheckoutItemsPrice(order.items)
    const bagsTotal = sumShoppingBagsPrice(order.shopping_bags)
    const amount =
      prev +
      Math.floor(itemsTotal * PRODUCT_TAX_RATE) +
      Math.floor(bagsTotal * SHOPPING_BAG_TAX_RATE) +
      Math.floor((order.delivery_fee_amount || 0) * DELIVERY_FEE_TAX_RATE) +
      Math.floor((order.frozen_fee_amount || 0) * DELIVERY_FEE_TAX_RATE)
    return isIncludingDiscount ? amount - (order.discount_amount || 0) : amount
  }, 0)
}

export const sumOrdersOriginalPrice = (orders: OrderType[], isIncludingDiscount?: boolean): number => {
  return orders.reduce((prev, order) => {
    const itemsTotal = sumCheckoutItemsOriginalPrice(order.items)
    const bagsTotal = sumShoppingBagsPrice(order.shopping_bags)
    const amount =
      prev +
      Math.floor(itemsTotal * PRODUCT_TAX_RATE) +
      Math.floor(bagsTotal * SHOPPING_BAG_TAX_RATE) +
      Math.floor((order.delivery_fee_amount || 0) * DELIVERY_FEE_TAX_RATE) +
      Math.floor((order.frozen_fee_amount || 0) * DELIVERY_FEE_TAX_RATE)
    return isIncludingDiscount ? amount - (order.discount_amount || 0) : amount
  }, 0)
}

export const sumRevenuePrice = (revenue: RevenueType, isIncludingDiscount?: boolean): number => {
  const itemsTotal = sumCheckoutItemsPrice(revenue.items)
  const bagsTotal = sumShoppingBagsPrice(revenue.shopping_bags)
  const amount =
    Math.floor(itemsTotal * PRODUCT_TAX_RATE) +
    Math.floor(bagsTotal * SHOPPING_BAG_TAX_RATE) +
    Math.floor(revenue.delivery_fee_amount * DELIVERY_FEE_TAX_RATE) +
    Math.floor(revenue.frozen_fee_amount * DELIVERY_FEE_TAX_RATE)
  return isIncludingDiscount ? amount - revenue.discount_amount : amount
}

export const sumRevenueOriginalPrice = (revenue: RevenueType, isIncludingDiscount?: boolean): number => {
  const itemsTotal = sumCheckoutItemsOriginalPrice(revenue.items)
  const bagsTotal = sumShoppingBagsPrice(revenue.shopping_bags)
  const amount =
    Math.floor(itemsTotal * PRODUCT_TAX_RATE) +
    Math.floor(bagsTotal * SHOPPING_BAG_TAX_RATE) +
    Math.floor(revenue.delivery_fee_amount * DELIVERY_FEE_TAX_RATE) +
    Math.floor(revenue.frozen_fee_amount * DELIVERY_FEE_TAX_RATE)
  return isIncludingDiscount ? amount - revenue.discount_amount : amount
}

export const sumCheckoutItemsPrice = (items: CheckoutItemType[]): number => {
  return items.reduce((prev, item) => {
    return prev + item.price * item.quantity
  }, 0)
}

export const sumCheckoutItemsOriginalPrice = (items: CheckoutItemType[]): number => {
  return items.reduce((prev, item) => {
    return prev + (item.original_price || item.price) * item.quantity
  }, 0)
}

export const sumShoppingBagsPrice = (bags?: CheckoutShoppingBagType[]): number => {
  if (typeof bags === 'undefined') return 0
  return bags.reduce((prev, bag) => {
    // quantityが0の袋（購入されていない袋）は売上に含めない。レガシーデータでquantity未設定の場合は1枚として扱う
    return prev + bag.price * (bag.quantity ?? 1)
  }, 0)
}

const parseItemType = (type: string): string => {
  switch (type) {
    case 'daily':
      return '当日'
    case 'preorder':
      return '予約'
    case 'delivery':
      return '取り寄せ'
    default:
      return ''
  }
}

export const formatRevenueData = (
  revenueList: RevenueType[],
  accountingType: 'order' | 'deliver'
): RevenueReportOnOrder[] | RevenueReportOnDeliver[] => {
  if (accountingType === 'order') {
    const dataset: RevenueReportOnOrder[] = []
    let total = 0

    for (let i = 0; i < revenueList.length; i = (i + 1) | 0) {
      const revenue = revenueList[i]
      total += sumRevenuePrice(revenue, true)

      revenue.items.map((item) => {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: format(revenue.checkout_at.toDate(), 'yyyy/MM/dd'),
          time: format(revenue.checkout_at.toDate(), 'HH:mm'),
          bread_name: item.bread_name,
          type: parseItemType(item.type),
          price: item.price.toString(),
          quantity: item.quantity.toString(),
          is_sale: item.original_price && item.original_price !== item.price ? '1' : '0', // 元値と売値が異なる場合はセール
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      })

      // レジ袋金額の追加
      if (typeof revenue.shopping_bags !== 'undefined') {
        const namesSet: Set<string> = new Set()
        revenue.shopping_bags.map((bag) => namesSet.add(bag.name))
        const names = Array.from(namesSet)

        names.map((name) => {
          const bags = revenue.shopping_bags!.filter((bag) => bag.name === name)
          if (!bags || bags.length === 0) return
          // 同名の袋の購入枚数を合計する（quantity未設定のレガシーデータは1枚として扱う）
          const quantity = bags.reduce((prev, bag) => prev + (bag.quantity ?? 1), 0)
          if (quantity === 0) return
          dataset.push({
            basket_number: revenue.basket_number,
            user_id: revenue.user_id,
            date: format(revenue.checkout_at.toDate(), 'yyyy/MM/dd'),
            time: format(revenue.checkout_at.toDate(), 'HH:mm'),
            bread_name: name,
            type: '',
            price: bags[0].price.toString(),
            quantity: quantity.toString(),
            is_sale: '0',
            total: '',
            status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
          })
        })
      }

      // クーポン利用の追加
      if (revenue.discount_amount !== 0) {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: format(revenue.checkout_at.toDate(), 'yyyy/MM/dd'),
          time: format(revenue.checkout_at.toDate(), 'HH:mm'),
          bread_name: 'クーポン利用',
          type: '',
          price: `-${revenue.discount_amount}`,
          quantity: '1',
          is_sale: '0',
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      }

      // 送料の追加
      if (revenue.delivery_fee_amount !== 0) {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: format(revenue.checkout_at.toDate(), 'yyyy/MM/dd'),
          time: format(revenue.checkout_at.toDate(), 'HH:mm'),
          bread_name: '送料',
          type: '',
          price: revenue.delivery_fee_amount.toString(),
          quantity: '1',
          is_sale: '0',
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      }

      // クール便料金の追加
      if (revenue.frozen_fee_amount !== 0) {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: format(revenue.checkout_at.toDate(), 'yyyy/MM/dd'),
          time: format(revenue.checkout_at.toDate(), 'HH:mm'),
          bread_name: 'クール便料金',
          type: '',
          price: revenue.frozen_fee_amount.toString(),
          quantity: '1',
          is_sale: '0',
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      }

      // 次ループの受取番号と異なる場合は、受取番号毎の合計行を追加
      if (i + 1 === revenueList.length || revenue.basket_number !== revenueList[i + 1].basket_number) {
        dataset.push({
          basket_number: `受取番号【${revenue.basket_number}】 計`,
          user_id: '',
          date: '',
          time: '',
          bread_name: '',
          type: '',
          price: '',
          quantity: '',
          is_sale: '',
          total: total.toString(),
          status: '',
        })
        total = 0
      }
    }
    return dataset
  } else {
    const dataset: RevenueReportOnDeliver[] = []
    let total = 0

    for (let i = 0; i < revenueList.length; i = (i + 1) | 0) {
      const revenue = revenueList[i]
      total += sumRevenuePrice(revenue, true)

      // 注文情報の追加
      revenue.items.map((item) => {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: item.date,
          bread_name: item.bread_name,
          type: parseItemType(item.type),
          price: item.price.toString(),
          quantity: item.quantity.toString(),
          is_sale: item.original_price && item.original_price !== item.price ? '1' : '0', // 元値と売値が異なる場合はセール
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      })

      // レジ袋金額の追加
      if (typeof revenue.shopping_bags !== 'undefined') {
        const namesSet: Set<string> = new Set()
        revenue.shopping_bags.map((bag) => namesSet.add(bag.name))
        const names = Array.from(namesSet)

        names.map((name) => {
          const bags = revenue.shopping_bags?.filter((bag) => bag.name === name)
          if (!bags || bags.length === 0) return
          // 同名の袋の購入枚数を合計する（quantity未設定のレガシーデータは1枚として扱う）
          const quantity = bags.reduce((prev, bag) => prev + (bag.quantity ?? 1), 0)
          if (quantity === 0) return
          dataset.push({
            basket_number: revenue.basket_number,
            user_id: revenue.user_id,
            date: revenue.date,
            bread_name: name,
            type: '',
            price: bags[0].price.toString(),
            quantity: quantity.toString(),
            is_sale: '0',
            total: '',
            status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
          })
        })
      }

      // クーポン利用の追加
      if (revenue.discount_amount !== 0) {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: revenue.date,
          bread_name: 'クーポン利用',
          type: '',
          price: `-${revenue.discount_amount}`,
          quantity: '1',
          is_sale: '0',
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      }

      // 送料の追加
      if (revenue.delivery_fee_amount !== 0) {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: revenue.date,
          bread_name: '送料',
          type: '',
          price: revenue.delivery_fee_amount.toString(),
          quantity: '1',
          is_sale: '0',
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      }

      // クール便料金の追加
      if (revenue.frozen_fee_amount !== 0) {
        dataset.push({
          basket_number: revenue.basket_number,
          user_id: revenue.user_id,
          date: revenue.date,
          bread_name: 'クール便料金',
          type: '',
          price: revenue.frozen_fee_amount.toString(),
          quantity: '1',
          is_sale: '0',
          total: '',
          status: revenue.status === 'requires_capture' ? '仮売上' : '実売上',
        })
      }

      // 次ループの受取番号と異なる場合は、受取番号毎の合計行を追加
      if (i + 1 === revenueList.length || revenue.basket_number !== revenueList[i + 1].basket_number) {
        dataset.push({
          basket_number: `受取番号【${revenue.basket_number}】 計`,
          user_id: '',
          date: '',
          bread_name: '',
          type: '',
          price: '',
          quantity: '',
          is_sale: '',
          total: total.toString(),
          status: '',
        })
        total = 0
      }
    }

    return dataset
  }
}

export const exportRevenueReportByXlsx = (revenueList: RevenueType[], accountingType: 'order' | 'deliver'): void => {
  const filename = `sacri_${format(new Date(), 'yyyy年M月d日')}_売上詳細`
  const header =
    accountingType === 'order'
      ? '受取番号,ユーザーID,注文日,注文時刻,商品名,注文タイプ,単価(税抜),個数,セールフラグ,総売上(税込),決済ステータス\n'
      : '受取番号,ユーザーID,受取日,商品名,注文タイプ,単価(税抜),個数,セールフラグ,総売上(税込),決済ステータス\n'
  const dataset = formatRevenueData(revenueList, accountingType)
  // SheetをWorkBookに追加する
  const sheetToWorkbook = (sheet: XLSX.WorkSheet): XLSX.WorkBook => {
    const sheets = { Sheet1: sheet }
    return { SheetNames: ['Sheet1'], Sheets: sheets }
  }
  // データを受け取ってWorkBookを作成する
  const createWorkbook = (data: string[][]): XLSX.WorkBook => {
    return sheetToWorkbook(XLSX.utils.aoa_to_sheet(data))
  }
  // 文字列をArrayBufferに変換
  const stringToArrayBuffer = (value: string): ArrayBuffer => {
    const buf = new ArrayBuffer(value.length)
    const view = new Uint8Array(buf)
    for (let i = 0; i != value.length; i = (i + 1) | 0) {
      view[i] = value.charCodeAt(i) & 0xff
    }
    return buf
  }
  const rows: string[][] = [header.split(',')]
  for (let i = 0; i < dataset.length; i = (i + 1) | 0) {
    const data = dataset[i]
    if (data.quantity === '0') continue
    const values = Object.values(data)
    const row: string[] = []
    for (let j = 0; j < values.length; j = (j + 1) | 0) {
      const value = values[j]
      row.push(value)
    }
    rows.push(row)
  }
  const options: XLSX.WritingOptions = { type: 'binary' }
  const workbook = createWorkbook(rows)
  const output = XLSX.write(workbook, options)
  // WorkbookからBlobオブジェクトを生成
  const blob = new Blob([stringToArrayBuffer(output)], { type: 'application/octet-stream' })
  FileSaver.saveAs(blob, `${filename}.xlsx`)
}

export const exportRevenueReportByCsv = (revenueList: RevenueType[], accountingType: 'order' | 'deliver') => {
  const filename = `sacri_${format(new Date(), 'yyyy年M月d日')}_売上詳細`
  const header =
    accountingType === 'order'
      ? '受取番号,ユーザーID,注文日,注文時刻,商品名,注文タイプ,単価(税抜),個数,セールフラグ,総売上(税込),決済ステータス\n'
      : '受取番号,ユーザーID,受取日,商品名,注文タイプ,単価(税抜),個数,セールフラグ,総売上(税込),決済ステータス\n'
  const rows = formatRevenueData(revenueList, accountingType)
  const _rows: string[] = [header]
  for (let i = 0; i < rows.length; i = (i + 1) | 0) {
    const row = rows[i]
    if (row.quantity === '0') continue
    let strRow = ''
    const keys = Object.keys(row)
    const values = Object.values(row)
    for (let j = 0; j < values.length; j = (j + 1) | 0) {
      let value = values[j]
      // 0から始まる値の場合、CSVでは省略されるので=""で囲む
      if (/^0.+$/.test(keys[j])) value = `="${value}"`
      strRow += value
      // 最終列でなければ区切り文字を追加
      if (j + 1 !== values.length) {
        strRow += ','
      }
    }
    strRow += '\n'
    _rows.push(strRow)
  }
  // CSVの文字化け防止bom
  const bom = new Uint8Array([0xef, 0xbb, 0xbf])
  const blob = new Blob([bom, ..._rows], { type: 'text/csv;charset=utf-8' })
  FileSaver.saveAs(blob, `${filename}.csv`)
}

export const calcTransferFee = (availableAmount: number): number => {
  return BASE_TRANSFER_FEE + Math.floor(availableAmount * TRANSFER_FEE_RATE)
}
